'use client';

import { Accordion, AccordionItem } from '@coolblue-development/freddy';
import { Box, Flex, Typography } from '@coolblue-development/freddy';
import { InfoUSP } from '@coolblue-development/next-patterns/client';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import {
  SalesPrice,
  SalesPriceCurrent,
  SalesPriceFormer
} from '@coolblue-development/next-sales-price/client';
import {
  SummaryItemPaymentType,
  SummaryItemType
} from '@features/add-to-cart-page/components/SummaryStore/SummaryStore.types';
import { ProductSummary } from '@features/add-to-cart-page/typings/Product';
import { mapPersonalPrice } from '@features/add-to-cart-page/utils/mapPersonalPrice';
import { useTranslation } from '@i18n/useTranslation';

import type { SummaryItem } from '../../SummaryStore';
import { useSummaryStore } from '../../SummaryStore';
import { groupItemsByType } from './utils';

export default function ComplementaryItems({
  productSummary,
  isBusiness = false
}: {
  productSummary: ProductSummary;
  isBusiness?: boolean;
}) {
  const { t } = useTranslation('webshop.addToCart');
  const { items } = useSummaryStore();
  const { isInternalUser } = useRequestContext();

  if (items.length === 0) return null;

  const groupedItems = groupItemsByType(items);
  const accessories = groupedItems.accessory ?? [];
  const insurances = groupedItems.insurance ?? [];
  const extendedWarranties = groupedItems['extended-warranty'] ?? [];
  const extraServices = groupedItems.service ?? [];
  const promotions = groupedItems.promotion ?? [];
  const freeWiths = groupedItems['free-with'] ?? [];

  function renderNortonListItem({
    summaryItem,
    index
  }: {
    summaryItem: SummaryItem;
    index: number;
  }) {
    const nortonPromotion = productSummary?.productPlusPromotions?.find(
      promotion => promotion.type.toLowerCase() === 'norton'
    );

    if (!nortonPromotion) return null;

    return (
      <Flex justifyContent='space-between' gap={200} key={index}>
        <Box flexGrow={1}>
          <InfoUSP
            productTitle={nortonPromotion.title}
            promoValue={
              <SalesPrice
                currentPrice={<></>}
                formerPrice={
                  <SalesPriceFormer
                    price={nortonPromotion.products[0].listPrice}
                    prefix={t('worth')}
                  />
                }
              />
            }
            hideIcon
          />
        </Box>
        <ItemPrice item={summaryItem} />
      </Flex>
    );
  }

  function renderPromotionListItem({
    summaryItem,
    index
  }: {
    summaryItem: SummaryItem;
    index: number;
  }) {
    const promotionProduct = summaryItem.promotion?.products.find(
      product => product.id.toString() === summaryItem.id
    );

    if (!promotionProduct) return;

    return (
      <Flex justifyContent='space-between' gap={200} key={index}>
        <Box flexGrow={1}>
          <InfoUSP
            productTitle={promotionProduct.name}
            promoValue={
              <SalesPrice
                currentPrice={<></>}
                {...(promotionProduct.listPrice &&
                  promotionProduct.priceIncludingVat && {
                    formerPrice: (
                      <SalesPriceFormer
                        brandName={promotionProduct.brand?.name}
                        price={promotionProduct.listPrice - promotionProduct.priceIncludingVat}
                      />
                    )
                  })}
                size='small'
              />
            }
            suffix={t('discount')}
            hideIcon
          />
        </Box>
        <ItemPrice item={summaryItem} formerPrice={promotionProduct.listPrice} />
      </Flex>
    );
  }

  function renderGenericListItem({
    summaryItem,
    index
  }: {
    summaryItem: SummaryItem;
    index: number;
  }) {
    return (
      <Flex alignItems='flex-start' gap={200} key={index}>
        <Typography
          variant='h5'
          flexGrow={1}
          mb={0}
          fontWeight={400}
          component='span'
          lineHeight={500}
        >
          {summaryItem.quantity > 1 && (
            <Box color='subtle' display='inline' component='span'>
              {summaryItem.quantity}x{' '}
            </Box>
          )}

          {summaryItem.name}
        </Typography>

        {isBusiness ? (
          <Box flexShrink={0}>
            <ItemPrice item={summaryItem} isBusiness={isBusiness} />
          </Box>
        ) : (
          summaryItem.showPrice && <ItemPrice item={summaryItem} />
        )}
      </Flex>
    );
  }

  function renderGenericList({
    summaryItems,
    title
  }: {
    summaryItems: SummaryItem[];
    title: string;
  }) {
    const summaryItemsMap = summaryItems.map((summaryItem, index) => {
      if (summaryItem.id == 'norton') {
        return renderNortonListItem({ summaryItem, index });
      }

      if (summaryItem.type === SummaryItemType.Promotion) {
        return renderPromotionListItem({ summaryItem, index });
      }

      return renderGenericListItem({ summaryItem, index });
    });

    if (summaryItems.length === 0 || summaryItemsMap.filter(item => item !== null).length === 0) {
      return null;
    }

    if (summaryItems.length === 1) {
      return (
        <div>
          <Typography variant='h5' lineHeight='2.0rem' component='span'>
            {title}
          </Typography>
          <Flex
            direction='column'
            gap={200}
            data-cy={isInternalUser ? 'added-accessories-list' : undefined}
          >
            {summaryItemsMap}
          </Flex>
        </div>
      );
    }

    return (
      <Accordion arrowPosition='start'>
        <AccordionItem
          title={title}
          data-cy={isInternalUser ? 'added-accessories-list' : undefined}
        >
          <Flex direction='column' gap={200}>
            {summaryItemsMap}
          </Flex>
        </AccordionItem>
      </Accordion>
    );
  }

  function ItemPrice({
    item,
    formerPrice,
    isBusiness = false
  }: {
    item: SummaryItem;
    formerPrice?: number;
    isBusiness?: boolean;
  }) {
    const personalPrice = mapPersonalPrice(item.personalPrice, item.quantity);

    return isBusiness ? (
      <SalesPrice
        alignment='right'
        size='small'
        variant='business'
        currentPrice={
          <SalesPriceCurrent
            price={item.quantity * item.salesPrice.includingVat}
            priceExcludingVat={item.quantity * (item.salesPrice.excludingVat ?? 0)}
          />
        }
      />
    ) : (
      <SalesPrice
        size='small'
        variant={item.paymentType === SummaryItemPaymentType.Monthly ? 'subscription' : 'regular'}
        personalPrice={personalPrice}
        currentPrice={<SalesPriceCurrent price={item.quantity * item.salesPrice.includingVat} />}
        {...(formerPrice && {
          formerPrice: (
            <SalesPriceFormer
              brandName={item.brand?.name}
              price={formerPrice}
              prefix={t('worth')}
            />
          )
        })}
      />
    );
  }

  return (
    <Flex direction='column' gap={200}>
      {renderGenericList({ summaryItems: accessories, title: t('addedAccessories') })}
      {renderGenericList({ summaryItems: insurances, title: t('insurance') })}
      {renderGenericList({ summaryItems: extendedWarranties, title: t('extendedWarranty') })}
      {renderGenericList({ summaryItems: extraServices, title: t('extraServices') })}
      {renderGenericList({ summaryItems: promotions, title: t('promotions') })}
      {renderGenericList({ summaryItems: freeWiths, title: t('freeWiths') })}
    </Flex>
  );
}
